/**
 * User-visible copy for multi-instance Liquid Cooling list + per-unit editor (#996 Phase 3).
 * Mirrors coolingSystemConfigurationsSubsectionCopy.
 */

export const LIQUID_COOLING_CDU_CONFIGURATIONS_LIST_TITLE =
  'Liquid Cooling units';

export const LIQUID_COOLING_CDU_CONFIGURATIONS_LIST_SUBTITLE =
  'Each row is one liquid cooling configuration. Select a row to edit, or add a new unit below.';

export const LIQUID_COOLING_CDU_EDIT_GATE_CARD_SUBTITLE =
  'Liquid-to-chip intent gates the configuration fields below.';

export const LIQUID_COOLING_CDU_EDIT_DETAILS_CARD_SUBTITLE =
  'Placement, manifold scope, redundancy, leak detection, and description for this liquid cooling unit.';

export const LIQUID_COOLING_CDU_EDIT_ALL_UNITS_LABEL = 'All Liquid Cooling';

export const LIQUID_COOLING_CDU_EDIT_HEADER_TITLE_NEW = 'New Liquid Cooling';

export const LIQUID_COOLING_CDU_EDIT_HEADER_TITLE_VIEW = 'View Liquid Cooling';

export const LIQUID_COOLING_CDU_EDIT_HEADER_TITLE_EDIT = 'Edit Liquid Cooling';

export const LIQUID_COOLING_CDU_EDIT_HEADER_SUBTITLE_VIEW =
  'Liquid Cooling details';

export const LIQUID_COOLING_CDU_EDIT_HEADER_SUBTITLE_EDIT =
  'Fill in details below';

export const LIQUID_COOLING_CDU_SAVE_ERROR_TOAST =
  'Could not save liquid cooling. Please try again.';

export const LIQUID_COOLING_CDU_DELETE_ERROR_TOAST =
  'Could not delete liquid cooling. Please try again.';

export const LIQUID_COOLING_CDU_LIST_LOADING_LABEL =
  'Loading liquid cooling...';

export const LIQUID_COOLING_CDU_LIST_ERROR_LABEL =
  'Failed to load liquid cooling';

export const LIQUID_COOLING_CDU_SEGMENT_EXISTING_TITLE =
  'EXISTING LIQUID COOLING';

export const LIQUID_COOLING_CDU_SEGMENT_NEW_TITLE = 'NEW LIQUID COOLING';

export const LIQUID_COOLING_CDU_ROW_NO_DETAILS_LABEL =
  'No liquid cooling details yet';

export const LIQUID_COOLING_CDU_ROW_VIEW_HINT =
  'Tap to view liquid cooling details';

export const LIQUID_COOLING_CDU_ROW_EDIT_HINT = 'Tap to edit liquid cooling';

export const LIQUID_COOLING_CDU_ROW_MENU_HINT =
  'Opens liquid cooling actions menu';

export const LIQUID_COOLING_CDU_ROW_DRAG_HINT =
  'Drag to reorder liquid cooling';

/** Modal / overlay header — mirrors SmallUpsEditModal index suffix pattern. */
export function formatLiquidCoolingCduEditHeaderTitle(options: {
  isNew: boolean;
  readOnly?: boolean;
  displayIndex?: number;
}): string {
  const { isNew, readOnly = false, displayIndex } = options;
  if (isNew) {
    return LIQUID_COOLING_CDU_EDIT_HEADER_TITLE_NEW;
  }
  if (readOnly) {
    return displayIndex != null
      ? `${LIQUID_COOLING_CDU_EDIT_HEADER_TITLE_VIEW} #${displayIndex}`
      : LIQUID_COOLING_CDU_EDIT_HEADER_TITLE_VIEW;
  }
  return displayIndex != null
    ? `${LIQUID_COOLING_CDU_EDIT_HEADER_TITLE_EDIT} #${displayIndex}`
    : LIQUID_COOLING_CDU_EDIT_HEADER_TITLE_EDIT;
}

export const LIQUID_COOLING_CDU_EDIT_STATUS_EXISTING_LABEL = 'Existing';

export const LIQUID_COOLING_CDU_EDIT_STATUS_NEW_LABEL = 'New';

export const LIQUID_COOLING_CDU_EDIT_TYPE_LABEL = 'Liquid cooling type';

/** Display labels for {@link FormTwoOptionChoice} — map to `status` existing | new. */
export const LIQUID_COOLING_CDU_EDIT_TYPE_OPTIONS = [
  LIQUID_COOLING_CDU_EDIT_STATUS_EXISTING_LABEL,
  LIQUID_COOLING_CDU_EDIT_STATUS_NEW_LABEL,
] as const;

export const LIQUID_COOLING_CDU_EDIT_DELETE_LABEL = 'Delete';

export const LIQUID_COOLING_CDU_EDIT_SAVE_LABEL = 'Save Liquid Cooling';

/** Create Request / draft flow — clarifies save is local to the step list. */
export const LIQUID_COOLING_CDU_EDIT_SAVE_DRAFT_LABEL = 'Save Liquid Cooling';

export const LIQUID_COOLING_CDU_EMPTY_STATE_TITLE =
  'No Liquid Cooling added yet';

export const LIQUID_COOLING_CDU_ADD_BUTTON_LABEL = 'Add Liquid Cooling';

export const LIQUID_COOLING_CDU_EDIT_SAVE_DRAFT_HINT =
  'Adds this unit to the list. Save the request separately when ready.';

export const LIQUID_COOLING_CDU_ROW_ACTION_EDIT_LABEL = 'Edit';

export const LIQUID_COOLING_CDU_ROW_ACTION_VIEW_LABEL = 'View';

export const LIQUID_COOLING_CDU_ROW_ACTION_DUPLICATE_LABEL = 'Duplicate';

/** List row overflow menu — short action labels (editor footer uses {@link LIQUID_COOLING_CDU_EDIT_DELETE_LABEL}). */
export const LIQUID_COOLING_CDU_ROW_ACTION_DELETE_LABEL = 'Delete';

export const LIQUID_COOLING_CDU_DELETE_CONFIRM_TITLE =
  'Delete Liquid Cooling Configuration';

export const LIQUID_COOLING_CDU_DELETE_CONFIRM_MESSAGE =
  'Are you sure you want to delete this liquid cooling configuration? This cannot be undone.';

export const LIQUID_COOLING_CDU_DISCARD_UNSAVED_TITLE = 'Unsaved Changes';

export const LIQUID_COOLING_CDU_DISCARD_UNSAVED_MESSAGE =
  'You have unsaved changes. Discard them?';

export const LIQUID_COOLING_CDU_DISCARD_UNSAVED_KEEP_EDITING_LABEL = 'Stay';

export const LIQUID_COOLING_CDU_DISCARD_UNSAVED_DISCARD_LABEL = 'Discard';

/** Per-unit editor header metrics — name, type, and six per-instance fields (#996). */
export const LIQUID_COOLING_CDU_ADDITIONAL_INFORMATION_SUBTITLE =
  'Additional liquid cooling notes and supporting documents.';

export const LIQUID_COOLING_CDU_ADDITIONAL_INFORMATION_LABEL =
  'Additional Liquid Cooling Information';

export const LIQUID_COOLING_CDU_ADDITIONAL_INFORMATION_PLACEHOLDER =
  'Enter additional information';
